import type React from 'react';
import clsx from 'clsx';
import { Button } from '../ui/button';
import { Heading } from '../ui/heading';
import { Text, TextLink } from '../ui/text';

interface PageHeaderProps {
  eyebrow?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  backHref?: string;
  backLabel?: string;
  actions?: React.ReactNode;
  meta?: React.ReactNode;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  backHref,
  backLabel,
  actions,
  meta,
}: PageHeaderProps) {
  return (
    <header className="border-b border-zinc-800 pb-5">
      {backHref ? (
        <TextLink href={backHref}>{backLabel ?? '← Back'}</TextLink>
      ) : null}
      {eyebrow ? (
        <Text className={clsx(backHref && 'mt-4', 'text-xs font-medium uppercase tracking-[0.18em] text-emerald-300')}>
          {eyebrow}
        </Text>
      ) : null}
      <div className={clsx((backHref || eyebrow) && 'mt-2', 'flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between')}>
        <div className="min-w-0">
          <Heading>{title}</Heading>
          {description ? <Text className="mt-2 max-w-3xl">{description}</Text> : null}
          {meta ? <div className="mt-3 flex flex-wrap gap-2">{meta}</div> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

export function Panel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx('rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:p-5', className)}>
      {children}
    </section>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/60 p-6 text-center">
      <Heading level={3} className="text-lg">
        {title}
      </Heading>
      {body ? <Text className="mx-auto mt-2 max-w-xl text-sm">{body}</Text> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone = 'zinc',
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'zinc' | 'emerald' | 'red' | 'amber';
}) {
  const toneClass = {
    zinc: 'text-zinc-100',
    emerald: 'text-emerald-300',
    red: 'text-red-300',
    amber: 'text-amber-300',
  }[tone];

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
      <div className="text-xs font-medium uppercase tracking-[0.16em] text-zinc-500">
        {label}
      </div>
      <div className={clsx('mt-2 text-2xl font-semibold', toneClass)}>{value}</div>
      {hint ? <div className="mt-1 text-xs text-zinc-500">{hint}</div> : null}
    </div>
  );
}

export function InlineActions({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

/** A shimmering placeholder block. Match the size of the real content so the
 *  swap doesn't reflow the page. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={clsx('animate-pulse rounded-md bg-zinc-800/70', className)}
    />
  );
}

/** N skeleton cards in a responsive grid — matches the team / game list
 *  layout used across the public routes. */
export function SkeletonCards({
  count = 4,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div
      className={clsx('grid gap-4 md:grid-cols-2', className)}
      aria-busy="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
        >
          <div className="flex items-start gap-4">
            <Skeleton className="size-12 shrink-0" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function SecondaryButton({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Button href={href} outline>
      {children}
    </Button>
  );
}
